import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { Editor } from '@tiptap/react';
import type { TemplatingContextType } from '@tyro/api';
import { SearchInput, useDisclosure } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { ChevronDownIcon } from '@tyro/icons';
import { useMemo, useRef, useState } from 'react';
import { useVariablesQuery } from '../../api/variables';

type VariableSelectorProps = {
  contextValue: TemplatingContextType;
  editor: Editor | null;
  templateType?: string | null | undefined;
};

type ChipValueTypes = {
  label: string;
  value: string;
};

export function TemplateVariableSelector({
  contextValue,
  editor,
  templateType,
}: VariableSelectorProps) {
  const { t } = useTranslation(['common']);
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchString, setSearchString] = useState('');

  const { data: variables, isLoading } = useVariablesQuery({
    context: contextValue,
  });

  const filteredItems = useMemo(() => {
    let filteredVariables = variables;
    if (templateType === 'email' || templateType === 'sms') {
      filteredVariables = filteredVariables?.filter(
        (variable) =>
          !(
            variable.context === 'STUDENT' &&
            ['tenant_photo', 'avatar_url'].includes(variable.value)
          ),
      );
    }
    return searchString.length === 0
      ? filteredVariables
      : filteredVariables?.filter((variable) =>
          variable?.name?.toLowerCase().includes(searchString.toLowerCase()),
        );
  }, [contextValue, searchString, templateType, variables]);

  const handleChipInsertion = ({ label, value }: ChipValueTypes) => {
    editor?.chain().focus()?.addVariable({ label, value }).run();
  };

  const handleClose = () => {
    onClose();
    setSearchString('');
  };

  const id = 'simple-popper';

  return (
    <Box display="flex" alignItems="center" px={1}>
      <Tooltip title={t('common:templateVariables')} placement="bottom">
        <Button
          ref={anchorEl}
          variant="outlined"
          aria-describedby={id}
          aria-expanded={isOpen ? 'true' : undefined}
          aria-controls={isOpen ? 'simple-popper-menu' : undefined}
          aria-haspopup="true"
          endIcon={
            !isLoading && <ChevronDownIcon sx={{ color: 'slategrey' }} />
          }
          onClick={onOpen}
          sx={{
            backgroundColor: 'slate.contrastText',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderColor: 'slate.200',
            justifyContent: 'space-between',
            minWidth: '195px',
          }}
        >
          {isLoading ? (
            <CircularProgress size={16} sx={{ mr: 1 }} />
          ) : (
            <Typography color="slate.800" fontSize="14px">
              {t('common:addVariable')}
            </Typography>
          )}
        </Button>
      </Tooltip>
      {/* biome-ignore lint/a11y/useValidAriaRole: Need to reset the default role */}
      <Popper
        open={isOpen}
        anchorEl={anchorEl.current}
        role={undefined}
        placement="bottom"
        disablePortal
        sx={{
          zIndex: 'modal',
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Stack
            ref={setContainerRef}
            sx={({ palette }) => ({
              minHeight: 172,
              maxHeight: 'min(calc(100vh - 96px), 400px)',
              position: 'relative',
              overflow: 'auto',
              mt: 1,
              borderRadius: 1,
              boxShadow:
                '0 0 2px 0 rgba(100, 116, 139, 0.24),0px 20px 40px -4px rgba(100, 116, 139, 0.24)',
              backgroundColor: palette.background.paper,
            })}
          >
            <ListSubheader
              ref={headerRef}
              component={Stack}
              sx={{
                pt: 1,
                px: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(20px)',
              }}
            >
              <SearchInput
                value={searchString}
                onChange={(event) => {
                  setSearchString(event.target.value);
                }}
              />
            </ListSubheader>
            {variables?.length === 0 ? (
              <Stack
                sx={{
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 80,
                }}
              >
                <Typography
                  variant="body1"
                  component="span"
                  color="text.secondary"
                >
                  {t('common:noResultsFound')}
                </Typography>
              </Stack>
            ) : (
              containerRef && (
                <MenuList
                  variant="selectedMenu"
                  id={id}
                  aria-labelledby={id}
                  style={{
                    position: 'relative',
                  }}
                >
                  {filteredItems?.map((item) => (
                    <MenuItem
                      key={item?.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleChipInsertion({
                          label: item?.name ?? t('common:default'),
                          value: item?.value ?? t('common:defaultLabelValue'),
                        });
                        handleClose();
                      }}
                    >
                      {item?.name}
                    </MenuItem>
                  ))}
                </MenuList>
              )
            )}
          </Stack>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
